import React from 'react';

interface ActionButtonProps {
  onClick: () => void;
  children: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  onClick,
  children,
  loading = false,
  disabled = false,
  variant = 'primary',
  size = 'md',
  fullWidth = false
}) => {
  const isDisabled = loading || disabled;

  const widthClass = fullWidth ? "w-full" : "w-full sm:w-auto";
  const baseClasses = `${widthClass} font-bold rounded-xl transition-all duration-200 transform shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2`;
  
  const sizeClasses = {
    sm: "py-2 px-4 text-sm",
    md: "py-3 px-6 text-base",
    lg: "py-4 px-8 text-lg"
  };

  const variantClasses = {
    primary: isDisabled 
      ? "bg-gray-400 text-gray-300 cursor-not-allowed" 
      : "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white hover:scale-101 active:scale-95 focus:ring-orange-500",
    secondary: isDisabled
      ? "bg-gray-400 text-gray-300 cursor-not-allowed"
      : "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white hover:scale-101 active:scale-95 focus:ring-blue-500",
    danger: isDisabled
      ? "bg-gray-400 text-gray-300 cursor-not-allowed"
      : "bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white hover:scale-101 active:scale-95 focus:ring-red-500",
    success: isDisabled
      ? "bg-gray-400 text-gray-300 cursor-not-allowed"
      : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white hover:scale-101 active:scale-95 focus:ring-green-500"
  };
  
  const combinedClasses = `${baseClasses} ${sizeClasses[size]} ${variantClasses[variant]}`;

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={combinedClasses}
    >
      {loading ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="animate-spin h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading...
        </span>
      ) : (
        children
      )}
    </button>
  );
};

export default ActionButton;